import React, { useState, useCallback, useMemo } from 'react'
import {
  Box,
  Container,
  Flex,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Button,
  Icon,
  useToast,
  Text,
  Select,
  HStack,
  FormControl,
  FormLabel,
} from '@chakra-ui/react'
import { FiRefreshCw } from 'react-icons/fi'
import { useProductsProvider } from './hooks/useProductsProvider'
import { StatusCard } from './components/StatusCard'
import { ProductTable } from './components/ProductTable'

export function AtualizacaoProdutosFornecedor() {
  const [fornecedor, setFornecedor] = useState('')
  const { data, isLoading, isError, refetch, isFetching } = useProductsProvider(fornecedor)
  const toast = useToast()
  const [tabIndex, setTabIndex] = useState(2) // Iniciar com aba de "Desatualizados" (índice 2)

  const fornecedores = useMemo(() => {
    if (!data) return []

    const uniqueProviders = new Set()
    if (data.atualizados) {
      data.atualizados.forEach((product) => {
        if (product.fornecedor) uniqueProviders.add(product.fornecedor)
      })
    }
    if (data.desatualizados) {
      data.desatualizados.forEach((product) => {
        if (product.fornecedor) uniqueProviders.add(product.fornecedor)
      })
    }

    return Array.from(uniqueProviders)
  }, [data])

  const handleRefetch = useCallback(() => {
    refetch()
    toast({
      title: 'Atualizando dados',
      description: 'Os dados estão sendo atualizados.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    })
  }, [refetch, toast])

  const handleFornecedorChange = (e) => {
    setFornecedor(e.target.value)
  }

  const allProducts = useMemo(() => {
    if (!data) return []
    return [...(data.atualizados || []), ...(data.desatualizados || [])]
  }, [data])

  const updatedProducts = useMemo(() => data?.atualizados || [], [data])
  const outdatedProducts = useMemo(() => data?.desatualizados || [], [data])

  return (
    <Box bg="gray.50" minH="100vh" py={6}>
      <Container maxW="1366px" px={4}>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.700">
            Controle de Produtos Fornecedor
          </Heading>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            colorScheme="blue"
            onClick={handleRefetch}
            isLoading={isFetching}
            loadingText="Atualizando"
            size="sm"
          >
            Atualizar
          </Button>
        </Flex>
        <Flex mb={6} alignItems="center" justifyContent="flex-end">
          <HStack spacing={4}>
            <FormControl id="fornecedor" maxW="250px">
              <FormLabel fontSize="sm">Filtrar por Fornecedor</FormLabel>
              <Select placeholder="Todos os fornecedores" value={fornecedor} onChange={handleFornecedorChange} size="sm">
                <option value="SPOT">SPOT</option>
                <option value="ASIA">ASIA</option>
              </Select>
            </FormControl>
          </HStack>
        </Flex>
        <StatusCard totalAtualizado={data?.atualizados?.length || 0} totalDesatualizado={data?.desatualizados?.length || 0} isLoading={isLoading} />
        {isError ? (
          <Box bg="red.50" p={4} borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500">
            <Text>Ocorreu um erro ao carregar os produtos. Por favor, tente novamente.</Text>
          </Box>
        ) : (
          <Tabs colorScheme="blue" variant="enclosed" isLazy index={tabIndex} onChange={setTabIndex}>
            <TabList>
              <Tab _selected={{ color: 'blue.600', bg: 'white', borderBottomColor: 'white', fontWeight: 'bold' }}>Todos ({allProducts.length})</Tab>
              <Tab _selected={{ color: 'green.600', bg: 'white', borderBottomColor: 'white', fontWeight: 'bold' }}>
                Atualizados ({updatedProducts.length})
              </Tab>
              <Tab _selected={{ color: 'orange.600', bg: 'white', borderBottomColor: 'white', fontWeight: 'bold' }}>
                Desatualizados ({outdatedProducts.length})
              </Tab>
            </TabList>

            <TabPanels bg="transparent">
              <TabPanel px={0}>
                {tabIndex === 0 && <ProductTable products={allProducts} isLoading={isLoading} title="Todos os Produtos" colorScheme="blue" />}
              </TabPanel>
              <TabPanel px={0}>
                {tabIndex === 1 && <ProductTable products={updatedProducts} isLoading={isLoading} title="Produtos Atualizados" colorScheme="green" />}
              </TabPanel>
              <TabPanel px={0}>
                {tabIndex === 2 && (
                  <ProductTable products={outdatedProducts} isLoading={isLoading} title="Produtos Desatualizados" colorScheme="orange" />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}
      </Container>
    </Box>
  )
}
